import { can, useAuth } from '@hostesa/auth-client';
import { FeatureFlagProvider } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import { RealtimeProvider } from '@hostesa/realtime-client';
import { LoadingState } from '@hostesa/ui';
import { useParams } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { HotelContextProvider } from '../lib/hotel-context';
import { useRuntime } from '../lib/runtime-context';
import { resolveHotelContext } from '../lib/slug-resolution';
import { NoAccessPage } from '../routes/standalone-pages';
import { HotelLayout } from './hotel-layout';

/**
 * Resolves the `:tenantSlug/:hotelSlug` route params to backend identifiers and
 * enforces the RBAC + ABAC gate, then composes the hotel-scoped provider stack:
 *
 *   FeatureFlagProvider (per-hotel flags) > RealtimeProvider (hotel rooms) >
 *   HotelContextProvider > HotelLayout (AppShell)
 *
 * Resolution runs in React (not a route loader) because memberships live in the
 * in-memory auth session. While the session is restoring we show a loading
 * state; an unresolved/unscoped slug renders the opaque no-access state that
 * never reveals cross-tenant existence.
 */
export function HotelGuard() {
  const { t } = useT('common');
  const { tenantSlug, hotelSlug } = useParams({ from: '/$tenantSlug/$hotelSlug' });
  const { memberships, loading, setActiveMembershipFilter } = useAuth();
  const runtime = useRuntime();

  const resolved = useMemo(
    () => resolveHotelContext(memberships, tenantSlug, hotelSlug),
    [memberships, tenantSlug, hotelSlug],
  );

  // Bind the active membership so useAuth().can()/canInScope() target this hotel.
  useEffect(() => {
    if (resolved) {
      setActiveMembershipFilter({
        tenantId: resolved.tenantId,
        hotelId: resolved.hotelId,
      });
    }
    return () => setActiveMembershipFilter(null);
  }, [resolved, setActiveMembershipFilter]);

  // Per-hotel feature flags (gap: backend does not yet expose per-hotel
  // overrides, so we resolve with the hotel id and rely on env/defaults).
  const flags = useMemo(
    () => runtime.flagEvaluator.resolve(resolved?.hotelId),
    [runtime.flagEvaluator, resolved],
  );

  if (loading) {
    return <LoadingState data-testid="hotel-loading" title={t('states.loadingTitle')} />;
  }

  // ABAC: no membership covers this tenant/hotel → opaque no-access.
  // Also require the baseline hotel:read RBAC permission to enter at all.
  if (!resolved || !can(resolved.membership.role, 'hotel:read')) {
    return <NoAccessPage />;
  }

  return (
    <FeatureFlagProvider flags={flags}>
      <RealtimeProvider
        url={runtime.config.realtimeUrl}
        getToken={() => runtime.sessionStore.getToken() ?? ''}
        queryClient={runtime.queryClient}
      >
        <HotelContextProvider value={resolved}>
          <HotelLayout />
        </HotelContextProvider>
      </RealtimeProvider>
    </FeatureFlagProvider>
  );
}
